import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { FileText, MessageSquare, TrendingUp, Users, Plus, AlertCircle, CreditCard, User, Globe, Building2, X, Eye, Megaphone, Ship, Anchor } from 'lucide-react';
import Header from '../components/Header';
import ProfileAnalytics from '../components/ProfileAnalytics';
import api from '../utils/api';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ rfqs: 0, quotes: 0, messages: 0 });
  const [company, setCompany] = useState(null);
  const [recentRfqs, setRecentRfqs] = useState([]);
  const [allRfqs, setAllRfqs] = useState([]);
  const [allQuotes, setAllQuotes] = useState([]);
  const [networkStats, setNetworkStats] = useState({ active_members: 0, countries: 0, active_rfqs: 0 });
  
  // Modal states
  const [showRfqsModal, setShowRfqsModal] = useState(false);
  const [showQuotesModal, setShowQuotesModal] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [companyRes, rfqsRes, quotesRes, networkRes] = await Promise.all([
          api.get('/companies/me').catch(() => ({ data: null })),
          api.get('/rfqs/my').catch(() => ({ data: [] })),
          api.get('/quotes/my').catch(() => ({ data: [] })),
          api.get('/network/stats').catch(() => ({ data: { active_members: 0, countries: 0, active_rfqs: 0 } }))
        ]);
        
        if (companyRes.data) setCompany(companyRes.data);
        const rfqData = rfqsRes.data || [];
        setAllRfqs(rfqData);
        setRecentRfqs(rfqData.slice(0, 5));
        const quoteData = quotesRes.data || [];
        setAllQuotes(quoteData);
        setStats({
          rfqs: rfqData.length,
          quotes: quoteData.length,
          messages: 0
        });
        setNetworkStats(networkRes.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      }
    };
    loadData();
  }, []);

  const isPending = company?.status === 'pending';

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-outfit font-bold text-ocean-900">Dashboard</h1>
            <p className="text-sm sm:text-base text-slate-600 mt-1 sm:mt-2">Welcome back, {user?.full_name}!</p>
          </div>

          {isPending && (
            <Card className="mb-6 bg-orange-50 border-orange-200">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-6 w-6 text-orange-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-orange-900 mb-1">Account Pending Approval</h3>
                    <p className="text-sm text-orange-800">
                      Your company profile is under review. You&apos;ll receive full access once approved by our team.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
            {/* My RFQs - Clickable */}
            <Card 
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setShowRfqsModal(true)}
              data-testid="rfqs-stat-card"
            >
              <CardContent className="p-3 sm:p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-600 mb-0.5 sm:mb-1">My RFQs</p>
                    <p className="text-2xl sm:text-3xl font-bold text-cyan-500">{stats.rfqs}</p>
                    <p className="text-[10px] sm:text-xs text-slate-400 mt-0.5 sm:mt-1">Click to view</p>
                  </div>
                  <FileText className="h-8 w-8 sm:h-10 sm:w-10 text-cyan-200" />
                </div>
              </CardContent>
            </Card>

            {/* Quotes Sent - Clickable */}
            <Card 
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setShowQuotesModal(true)}
              data-testid="quotes-stat-card"
            >
              <CardContent className="p-3 sm:p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-600 mb-0.5 sm:mb-1">Quotes Sent</p>
                    <p className="text-2xl sm:text-3xl font-bold text-green-500">{stats.quotes}</p>
                    <p className="text-[10px] sm:text-xs text-slate-400 mt-0.5 sm:mt-1">Click to view</p>
                  </div>
                  <TrendingUp className="h-8 w-8 sm:h-10 sm:w-10 text-green-200" />
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/messages')}>
              <CardContent className="p-3 sm:p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-600 mb-0.5 sm:mb-1">Messages</p>
                    <p className="text-2xl sm:text-3xl font-bold text-purple-500">{stats.messages}</p>
                  </div>
                  <MessageSquare className="h-8 w-8 sm:h-10 sm:w-10 text-purple-200" />
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/directory')}>
              <CardContent className="p-3 sm:p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-600 mb-0.5 sm:mb-1">Network</p>
                    <p className="text-2xl sm:text-3xl font-bold text-blue-500">{networkStats.active_members || 0}</p>
                    <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 sm:mt-1">{networkStats.countries || 0} countries</p>
                  </div>
                  <Users className="h-8 w-8 sm:h-10 sm:w-10 text-blue-200" />
                </div>
              </CardContent>
            </Card>

            {/* Profile Views - New */}
            <ProfileAnalytics compact={true} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0 space-y-2 sm:space-y-3">
                <Button
                  onClick={() => navigate('/rfqs/create')}
                  className="w-full bg-cyan-500 hover:bg-cyan-600 justify-start text-sm sm:text-base h-10 sm:h-11"
                  disabled={isPending}
                  data-testid="quick-create-rfq"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create New RFQ
                </Button>
                <Button
                  onClick={() => navigate('/charters/create')}
                  className="w-full bg-blue-500 hover:bg-blue-600 justify-start text-sm sm:text-base h-10 sm:h-11"
                  disabled={isPending}
                  data-testid="quick-create-charter"
                >
                  <Ship className="mr-2 h-4 w-4" />
                  Request Vessel Charter
                </Button>
                <Button
                  onClick={() => navigate('/quotes/create')}
                  className="w-full bg-green-500 hover:bg-green-600 justify-start text-sm sm:text-base h-10 sm:h-11"
                  disabled={isPending}
                  data-testid="quick-create-quote"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create New Quote
                </Button>
                <Button
                  onClick={() => navigate('/fleet')}
                  variant="outline"
                  className="w-full justify-start text-sm sm:text-base h-10 sm:h-11"
                  disabled={isPending}
                  data-testid="manage-fleet"
                >
                  <Anchor className="mr-2 h-4 w-4" />
                  Manage Fleet
                </Button>
                <Button
                  onClick={() => navigate('/advertising')}
                  className="w-full bg-purple-500 hover:bg-purple-600 justify-start text-sm sm:text-base h-10 sm:h-11"
                  disabled={isPending}
                  data-testid="quick-create-ad"
                >
                  <Megaphone className="mr-2 h-4 w-4" />
                  Create an Ad & Pay
                </Button>
                <Button
                  onClick={() => navigate('/profile')}
                  variant="outline"
                  className="w-full justify-start text-sm sm:text-base h-10 sm:h-11"
                  data-testid="update-profile-button"
                >
                  <User className="mr-2 h-4 w-4" />
                  Update Profile
                </Button>
                <Button
                  onClick={() => navigate('/billing')}
                  variant="outline"
                  className="w-full justify-start text-sm sm:text-base h-10 sm:h-11"
                  data-testid="manage-subscription-button"
                >
                  <CreditCard className="mr-2 h-4 w-4" />
                  Manage Subscription
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg">Company Status</CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs sm:text-sm text-slate-600">Verification Status</span>
                    <Badge className={`text-xs ${
                      company?.status === 'active' ? 'bg-green-100 text-green-700' :
                      company?.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {company?.status?.toUpperCase() || 'PENDING'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs sm:text-sm text-slate-600">Subscription</span>
                    <Badge className="bg-cyan-100 text-cyan-700 text-xs">
                      {company?.subscription_plan?.toUpperCase() || 'TRIAL'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs sm:text-sm text-slate-600">Services</span>
                    <span className="text-xs sm:text-sm font-medium">{company?.services?.length || 0} active</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {/* Recent RFQs - 2 columns */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                    <CardTitle className="text-base sm:text-lg">Recent RFQs</CardTitle>
                    <Button onClick={() => navigate('/rfqs')} variant="outline" size="sm" className="text-xs sm:text-sm">
                      View All
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
                  {recentRfqs.length === 0 ? (
                    <div className="text-center py-6 sm:py-8 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl">
                      <FileText className="h-10 w-10 sm:h-12 sm:w-12 mx-auto text-cyan-300 mb-3" />
                      <p className="text-slate-600 text-sm sm:text-base font-medium">No RFQs yet</p>
                      <p className="text-slate-500 text-xs mt-1 mb-4">Start by creating your first Request for Quote</p>
                      <Button
                        onClick={() => navigate('/rfqs/create')}
                        className="bg-cyan-500 hover:bg-cyan-600 text-sm"
                        disabled={isPending}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create your first RFQ
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2 sm:space-y-3">
                      {recentRfqs.map((rfq) => (
                        <div
                          key={rfq.id}
                          className="flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors gap-2"
                          onClick={() => navigate(`/rfqs/${rfq.id}`)}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm sm:text-base truncate">{rfq.title || rfq.cargo_type}</p>
                            <p className="text-xs sm:text-sm text-slate-500 truncate">{rfq.origin} → {rfq.destination}</p>
                          </div>
                          <div className="flex items-center justify-between sm:justify-end sm:text-right gap-2">
                            <Badge className={`text-xs ${
                              rfq.status === 'open' ? 'bg-green-100 text-green-700' :
                              rfq.status === 'closed' ? 'bg-slate-100 text-slate-700' :
                              'bg-cyan-100 text-cyan-700'
                            }`}>
                              {rfq.status}
                            </Badge>
                            <p className="text-xs text-slate-400">
                              {new Date(rfq.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Profile Analytics - 1 column */}
            <div>
              <ProfileAnalytics />
            </div>
          </div>
        </div>
      </div>

      {/* RFQs Modal */}
      <Dialog open={showRfqsModal} onOpenChange={setShowRfqsModal}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[85vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-500" />
              My RFQs ({allRfqs.length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 sm:space-y-3 mt-3 sm:mt-4">
            {allRfqs.length === 0 ? (
              <p className="text-center text-slate-500 py-6 sm:py-8 text-sm">No RFQs found</p>
            ) : (
              allRfqs.map((rfq) => (
                <div
                  key={rfq.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 rounded-lg border hover:bg-slate-50 cursor-pointer transition-colors gap-2"
                  onClick={() => { setShowRfqsModal(false); navigate(`/rfqs/${rfq.id}`); }}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm sm:text-base truncate">{rfq.title || rfq.cargo_type}</p>
                    <p className="text-xs sm:text-sm text-slate-500 truncate">{rfq.origin} → {rfq.destination}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Created: {new Date(rfq.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-2">
                    <Badge className={`text-xs ${
                      rfq.status === 'open' ? 'bg-green-100 text-green-700' :
                      rfq.status === 'closed' ? 'bg-slate-100 text-slate-700' :
                      'bg-cyan-100 text-cyan-700'
                    }`}>
                      {rfq.status}
                    </Badge>
                    <p className="text-xs text-slate-400">{rfq.quotes_count || 0} quotes</p>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowRfqsModal(false)} className="w-full sm:w-auto">Close</Button>
            <Button onClick={() => { setShowRfqsModal(false); navigate('/rfqs'); }} className="w-full sm:w-auto bg-cyan-500 hover:bg-cyan-600">
              View All RFQs
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quotes Modal */}
      <Dialog open={showQuotesModal} onOpenChange={setShowQuotesModal}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[85vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-green-500" />
              My Quotes ({allQuotes.length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 sm:space-y-3 mt-3 sm:mt-4">
            {allQuotes.length === 0 ? (
              <p className="text-center text-slate-500 py-6 sm:py-8 text-sm">No quotes sent yet</p>
            ) : (
              allQuotes.map((quote) => (
                <div
                  key={quote.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 rounded-lg border hover:bg-slate-50 cursor-pointer transition-colors gap-2"
                  onClick={() => { setShowQuotesModal(false); navigate('/quotes'); }}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm sm:text-base truncate">{quote.rfq_title || 'Quote'}</p>
                    <p className="text-xs sm:text-sm text-slate-500 truncate">
                      {quote.origin} → {quote.destination}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Sent: {new Date(quote.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-2">
                    <Badge className={`text-xs ${
                      quote.status === 'accepted' ? 'bg-green-100 text-green-700' :
                      quote.status === 'declined' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {quote.status}
                    </Badge>
                    {quote.total_price && (
                      <p className="text-sm font-semibold text-slate-700">
                        ${quote.total_price?.toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowQuotesModal(false)} className="w-full sm:w-auto">Close</Button>
            <Button onClick={() => { setShowQuotesModal(false); navigate('/quotes'); }} className="w-full sm:w-auto bg-green-500 hover:bg-green-600">
              View All Quotes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Dashboard;
